import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { inboxApi } from "./api";
import { inboxKeys } from "./hooks";
import type { InboxPreferences, MarkReadMode } from "../types";

const DEFAULT_MARK_READ_MODE: MarkReadMode = "on_open";

export const useInboxReadPreference = () => {
  const query = useQuery({
    queryKey: inboxKeys.preferences(),
    queryFn: () => inboxApi.getPreferences(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...query,
    markReadMode: query.data?.mark_read_mode ?? DEFAULT_MARK_READ_MODE,
    isResolved: Boolean(query.data)
  };
};

export const useSetInboxReadPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...inboxKeys.preferences(), "UPDATE"],
    mutationFn: (mode: MarkReadMode) => inboxApi.updatePreferences(mode),
    onMutate: async (mode) => {
      await queryClient.cancelQueries({ queryKey: inboxKeys.preferences() });
      const previous = queryClient.getQueryData<InboxPreferences>(
        inboxKeys.preferences()
      );
      queryClient.setQueryData<InboxPreferences>(inboxKeys.preferences(), {
        mark_read_mode: mode
      });
      return { previous };
    },
    onError: (_err, _mode, context) => {
      if (context?.previous)
        queryClient.setQueryData(inboxKeys.preferences(), context.previous);
      toast.error("Failed to update read preference");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.preferences() });
    }
  });
};
